import React, { useState, useEffect, useCallback } from 'react';
import { History, Loader2, ChevronDown, ChevronUp, UserPlus, FileText, Trash2, PlayCircle, GitCompare, X, Star, Pencil, Check, StickyNote } from 'lucide-react';
import { toast } from 'sonner';
import { isSessionExpired } from '../../utils/adminSession';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const youtubeThumb = (url) => {
  const m = (url || '').match(/(?:v=|youtu\.be\/|embed\/)([a-zA-Z0-9_-]{11})/);
  return m ? `https://img.youtube.com/vi/${m[1]}/mqdefault.jpg` : null;
};

const SessionExercisesList = ({ sessions }) => (
  <div className="space-y-3">
    {sessions.map((s, i) => (
      <div key={i}>
        <p className="text-xs font-bold text-blue-300 mb-1">{s.session_label}</p>
        <ul className="space-y-1">
          {s.exercises.map((ex) => {
            const thumb = youtubeThumb(ex.youtube_url);
            return (
              <li key={ex.exercise_id} className="text-xs text-zinc-400 flex items-center gap-2 flex-wrap">
                {ex.image_url ? (
                  <img src={`${BACKEND_URL}${ex.image_url}`} alt={ex.name} loading="lazy" className="w-9 h-9 rounded object-cover border border-white/10 flex-shrink-0" />
                ) : (
                  <FileText className="w-3 h-3 text-zinc-600 flex-shrink-0" />
                )}
                <span className="text-zinc-300">{ex.name}</span>
                <span className="text-zinc-600">· {ex.sets_count}×{ex.reps_scheme} · {ex.rest_seconds}s{ex.tempo ? ` · tempo ${ex.tempo}` : ''}</span>
                {thumb && (
                  <a href={ex.youtube_url} target="_blank" rel="noreferrer" className="relative flex-shrink-0 group" title="Voir la vidéo YouTube" data-testid={`ai-history-video-${ex.exercise_id}`}>
                    <img src={thumb} alt="" className="w-16 h-9 rounded object-cover border border-white/10" />
                    <PlayCircle className="w-4 h-4 text-white absolute inset-0 m-auto drop-shadow group-hover:scale-110 transition-transform" />
                  </a>
                )}
              </li>
            );
          })}
        </ul>
      </div>
    ))}
  </div>
);

/** Historique PERMANENT de toutes les générations IA (assignées ou non) — le coach peut
 * retrouver et réutiliser n'importe quelle génération passée, même déjà assignée. */
export const AIHistoryPanel = ({ csrfToken, clients }) => {
  const [history, setHistory] = useState(null);
  const [expanded, setExpanded] = useState(null);
  const [assignEmail, setAssignEmail] = useState({});
  const [busyId, setBusyId] = useState(null);
  const [compareIds, setCompareIds] = useState([]);
  const [showCompare, setShowCompare] = useState(false);
  const [renamingId, setRenamingId] = useState(null);
  const [renameValue, setRenameValue] = useState('');
  const [notesDraft, setNotesDraft] = useState({});


  const fetchWithCsrf = (url, options = {}) => fetch(url, {
    ...options, credentials: 'include',
    headers: { ...options.headers, 'X-CSRF-Token': csrfToken },
  });

  const load = useCallback(() => {
    fetch(`${BACKEND_URL}/api/admin/programs-pro/ai-history`, { credentials: 'include' })
      .then((r) => (isSessionExpired(r) ? Promise.reject(null) : r.json()))
      .then((d) => setHistory(d.history || [])).catch(() => setHistory([]));
  }, []);
  useEffect(() => { load(); }, [load]);

  const assign = (historyId) => {
    const email = assignEmail[historyId];
    if (!email) { toast.error('Choisissez un client.'); return; }
    setBusyId(historyId);
    fetchWithCsrf(`${BACKEND_URL}/api/admin/programs-pro/ai-history/${historyId}/assign`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ email }),
    })
      .then((r) => {
        if (isSessionExpired(r)) return Promise.reject(null);
        return r.ok ? r.json() : r.json().then((j) => Promise.reject(j));
      })
      .then(() => { toast.success('Programme assigné et client notifié !'); load(); })
      .catch((j) => { if (j) toast.error(j?.detail || "Échec de l'assignation."); })
      .finally(() => setBusyId(null));
  };

  const del = (historyId) => {
    if (!window.confirm('Supprimer définitivement cette génération de l\'historique ?')) return;
    setBusyId(historyId);
    fetchWithCsrf(`${BACKEND_URL}/api/admin/programs-pro/ai-history/${historyId}`, { method: 'DELETE' })
      .then((r) => {
        if (isSessionExpired(r)) return Promise.reject(null);
        return r.ok ? r.json() : Promise.reject(r);
      })
      .then(() => { load(); toast.success('Entrée supprimée de l\'historique.'); })
      .catch((r) => { if (r) toast.error('Échec de la suppression.'); })
      .finally(() => setBusyId(null));
  };

  const toggleFavorite = (historyId) => {
    fetchWithCsrf(`${BACKEND_URL}/api/admin/programs-pro/ai-history/${historyId}/favorite`, { method: 'POST' })
      .then((r) => {
        if (isSessionExpired(r)) return Promise.reject(null);
        return r.ok ? r.json() : Promise.reject(r);
      })
      .then(() => load())
      .catch((r) => { if (r) toast.error('Échec de la mise à jour du favori.'); });
  };

  const startRename = (h) => { setRenamingId(h.history_id); setRenameValue(h.name); };

  const saveRename = (historyId) => {
    if (!renameValue.trim()) { toast.error('Le nom du modèle ne peut pas être vide.'); return; }
    setBusyId(historyId);
    fetchWithCsrf(`${BACKEND_URL}/api/admin/programs-pro/ai-history/${historyId}/rename`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ name: renameValue.trim() }),
    })
      .then((r) => {
        if (isSessionExpired(r)) return Promise.reject(null);
        return r.ok ? r.json() : Promise.reject(r);
      })
      .then(() => { toast.success('Modèle renommé !'); setRenamingId(null); load(); })
      .catch((r) => { if (r) toast.error('Échec du renommage.'); })
      .finally(() => setBusyId(null));
  };

  const toggleCompare = (historyId) => {
    setCompareIds((prev) => {
      if (prev.includes(historyId)) return prev.filter((id) => id !== historyId);
      if (prev.length >= 2) { toast.info('Choisissez au maximum 2 générations à comparer.'); return prev; }
      return [...prev, historyId];
    });
  };

  const saveNotes = (historyId) => {
    const notes = notesDraft[historyId] ?? '';
    setBusyId(historyId);
    fetchWithCsrf(`${BACKEND_URL}/api/admin/programs-pro/ai-history/${historyId}/notes`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ notes }),
    })
      .then((r) => {
        if (isSessionExpired(r)) return Promise.reject(null);
        return r.ok ? r.json() : Promise.reject(r);
      })
      .then(() => { toast.success('Note enregistrée !'); load(); })
      .catch((r) => { if (r) toast.error("Échec de l'enregistrement de la note."); })
      .finally(() => setBusyId(null));
  };

  return (
    <div className="rounded-2xl bg-zinc-900/60 border border-blue-500/20 p-5 mb-5" data-testid="ai-history-panel">
      <h3 className="text-lg font-bold text-white flex items-center gap-2 mb-4">
        <History className="w-5 h-5 text-blue-400" /> Historique IA
        {history && history.length > 0 && <span className="text-xs font-normal text-zinc-500">({history.length})</span>}
      </h3>

      {history === null ? (
        <div className="flex justify-center py-6"><Loader2 className="w-5 h-5 text-blue-400 animate-spin" /></div>
      ) : history.length === 0 ? (
        <p className="text-sm text-zinc-500 py-4 text-center">Aucune génération IA pour l'instant.</p>
      ) : (
        <div className="space-y-2">
          {history.map((h) => (
            <div key={h.history_id} className="bg-zinc-800/60 border border-white/10 rounded-xl p-3" data-testid={`ai-history-card-${h.history_id}`}>
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <div>
                  {renamingId === h.history_id ? (
                    <div className="flex items-center gap-1.5">
                      <input value={renameValue} onChange={(e) => setRenameValue(e.target.value)} autoFocus
                        onKeyDown={(e) => { if (e.key === 'Enter') saveRename(h.history_id); if (e.key === 'Escape') setRenamingId(null); }}
                        className="bg-zinc-900 border border-amber-500/40 rounded-lg px-2 py-1 text-white text-sm" data-testid={`ai-history-rename-input-${h.history_id}`} />
                      <button onClick={() => saveRename(h.history_id)} disabled={busyId === h.history_id} className="p-1.5 text-emerald-400 hover:text-emerald-300" data-testid={`ai-history-rename-save-${h.history_id}`}>
                        <Check className="w-4 h-4" />
                      </button>
                      <button onClick={() => setRenamingId(null)} className="p-1.5 text-zinc-500 hover:text-white" data-testid={`ai-history-rename-cancel-${h.history_id}`}>
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  ) : (
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="text-white font-semibold">{h.name}</p>
                      {h.is_favorite && (
                        <button onClick={() => startRename(h)} className="text-zinc-500 hover:text-amber-300 transition-colors" title="Renommer ce modèle" data-testid={`ai-history-rename-btn-${h.history_id}`}>
                          <Pencil className="w-3.5 h-3.5" />
                        </button>
                      )}
                      {(h.assigned_emails || []).length > 0 && (
                        <span className="px-1.5 py-0.5 rounded text-[9px] bg-emerald-500/15 text-emerald-300 border border-emerald-500/25 font-bold" data-testid={`ai-history-assigned-badge-${h.history_id}`}>
                          DÉJÀ ASSIGNÉ ({h.assigned_emails.length}×)
                        </span>
                      )}
                      {h.notes && (
                        <span className="text-amber-300/70" title={h.notes} data-testid={`ai-history-notes-badge-${h.history_id}`}>
                          <StickyNote className="w-3.5 h-3.5" />
                        </span>
                      )}
                    </div>
                  )}
                  <p className="text-xs text-zinc-500 line-clamp-2">{h.goal} · {h.sessions.length} séance(s) · {h.weeks_target} sem. · {new Date(h.created_at).toLocaleDateString('fr-CH', { dateStyle: 'medium' })}</p>
                </div>
                <div className="flex items-center gap-2">
                  <button onClick={() => toggleFavorite(h.history_id)} className={`p-2 transition-colors ${h.is_favorite ? 'text-amber-400' : 'text-zinc-500 hover:text-amber-300'}`} title="Marquer comme favori" data-testid={`ai-history-favorite-btn-${h.history_id}`}>
                    <Star className="w-4 h-4" fill={h.is_favorite ? 'currentColor' : 'none'} />
                  </button>
                  <label className="flex items-center gap-1.5 text-[11px] text-zinc-400 cursor-pointer select-none" title="Sélectionner pour comparer">
                    <input type="checkbox" checked={compareIds.includes(h.history_id)} onChange={() => toggleCompare(h.history_id)}
                      className="w-3.5 h-3.5 accent-blue-500" data-testid={`ai-history-compare-check-${h.history_id}`} />
                    Comparer
                  </label>
                  <button onClick={() => setExpanded(expanded === h.history_id ? null : h.history_id)} className="p-2 text-zinc-400 hover:text-white transition-colors" data-testid={`ai-history-toggle-${h.history_id}`}>
                    {expanded === h.history_id ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                  </button>
                  <button onClick={() => del(h.history_id)} disabled={busyId === h.history_id} className="p-2 text-zinc-400 hover:text-red-400 transition-colors disabled:opacity-50" data-testid={`ai-history-delete-${h.history_id}`}>
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>

              {expanded === h.history_id && (
                <div className="mt-3 pt-3 border-t border-white/10 space-y-3" data-testid={`ai-history-detail-${h.history_id}`}>
                  {(h.assigned_emails || []).length > 0 && (
                    <p className="text-xs text-zinc-500">
                      Assigné à : {h.assigned_emails.map((a) => a.email).join(', ')}
                    </p>
                  )}
                  <SessionExercisesList sessions={h.sessions} />

                  {h.is_favorite && (
                    <div className="pt-2 border-t border-white/10">
                      <label className="text-xs text-zinc-500 mb-1 block">Note interne (visible coach uniquement)</label>
                      <textarea value={notesDraft[h.history_id] ?? h.notes ?? ''} onChange={(e) => setNotesDraft((n) => ({ ...n, [h.history_id]: e.target.value }))} rows={2}
                        placeholder="Ex: fonctionne bien pour reprise après blessure épaule..."
                        className="w-full bg-zinc-900 border border-white/10 rounded-lg px-3 py-2 text-white text-sm resize-none" data-testid={`ai-history-notes-input-${h.history_id}`} />
                      <button onClick={() => saveNotes(h.history_id)} disabled={busyId === h.history_id}
                        className="mt-1.5 inline-flex items-center gap-1.5 px-3 py-1.5 border border-white/15 text-zinc-300 text-xs font-semibold rounded-lg hover:border-white/30 transition-colors disabled:opacity-50"
                        data-testid={`ai-history-notes-save-${h.history_id}`}>
                        {busyId === h.history_id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : 'Enregistrer la note'}
                      </button>
                    </div>
                  )}

                  <div className="flex items-center gap-2 pt-2">
                    <select value={assignEmail[h.history_id] || ''} onChange={(e) => setAssignEmail((a) => ({ ...a, [h.history_id]: e.target.value }))}
                      className="flex-1 bg-zinc-900 border border-white/10 rounded-lg px-3 py-2 text-white text-sm" data-testid={`ai-history-assign-select-${h.history_id}`}>
                      <option value="">Choisir un client...</option>
                      {(clients || []).map((c) => <option key={c.email} value={c.email}>{c.name ? `${c.name} — ${c.email}` : c.email}</option>)}
                    </select>
                    <button onClick={() => assign(h.history_id)} disabled={busyId === h.history_id}
                      className="inline-flex items-center gap-1.5 px-3 py-2 bg-gradient-to-r from-primary to-pink-500 text-white text-sm font-bold rounded-lg hover:scale-105 transition-transform disabled:opacity-50"
                      data-testid={`ai-history-assign-btn-${h.history_id}`}>
                      {busyId === h.history_id ? <Loader2 className="w-4 h-4 animate-spin" /> : <UserPlus className="w-4 h-4" />} Réutiliser / Assigner
                    </button>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {compareIds.length === 2 && (
        <div className="sticky bottom-2 mt-4 flex justify-center">
          <button onClick={() => setShowCompare(true)}
            className="inline-flex items-center gap-2 px-4 py-2.5 bg-gradient-to-r from-blue-600 to-cyan-500 text-white text-sm font-bold rounded-xl shadow-lg hover:scale-105 transition-transform"
            data-testid="ai-history-compare-open-btn">
            <GitCompare className="w-4 h-4" /> Comparer les 2 générations sélectionnées
          </button>
        </div>
      )}

      {showCompare && compareIds.length === 2 && (
        <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4" data-testid="ai-history-compare-modal">
          <div className="bg-zinc-900 border border-white/10 rounded-2xl max-w-4xl w-full max-h-[85vh] overflow-y-auto p-5">
            <div className="flex items-center justify-between mb-4">
              <h4 className="text-white font-bold flex items-center gap-2"><GitCompare className="w-4 h-4 text-blue-400" /> Comparaison des générations IA</h4>
              <button onClick={() => setShowCompare(false)} className="p-1.5 text-zinc-400 hover:text-white" data-testid="ai-history-compare-close-btn"><X className="w-5 h-5" /></button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {compareIds.map((id) => {
                const h = history.find((x) => x.history_id === id);
                if (!h) return null;
                return (
                  <div key={id} className="bg-zinc-800/60 border border-white/10 rounded-xl p-4" data-testid={`ai-history-compare-column-${id}`}>
                    <p className="text-white font-bold mb-1">{h.name}</p>
                    <p className="text-xs text-zinc-500 mb-3">{h.goal} · {h.sessions.length} séance(s) · {h.weeks_target} sem. · +{h.tonnage_growth_target_pct}% tonnage</p>
                    <SessionExercisesList sessions={h.sessions} />
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AIHistoryPanel;
